import { redirect } from "@tanstack/react-router";

import { ensureCurrentUser } from "./currentUser";
import type { CurrentUser } from "./currentUser";

/**
 * Auth guard for TanStack Router `beforeLoad` hooks. Use as:
 *
 * ```ts
 * export const Route = createFileRoute("/foo")({
 *   beforeLoad: ({ context, location }) =>
 *     requireAuth(context.queryClient, location.href),
 *   component: FooPage,
 * });
 * ```
 *
 * Returns the resolved user (typed non-null) so the route loader /
 * component can read it from the route's `beforeLoadData` without
 * an extra `useCurrentUser` round-trip.
 *
 * On 401 (or null user) throws a `redirect` to `/login` with the
 * current URL preserved as `?returnTo=<encoded>`. TanStack Router
 * treats the throw as a redirect — the destination route resolves
 * before the React tree renders, so there's no "flash of protected
 * content" between the guard firing and the navigation.
 */
export async function requireAuth(
  queryClient: import("@tanstack/react-query").QueryClient,
  href: string,
): Promise<{ user: CurrentUser }> {
  const user = await ensureCurrentUser(queryClient);
  if (!user) {
    throw redirect({
      to: "/login",
      search: { returnTo: href },
    });
  }
  return { user };
}

/**
 * Admin gate. Layered on top of `requireAuth` — call this from a
 * route's `beforeLoad` when the route should only render for users
 * with `role === "admin"`. Unauth → /login. Auth-but-not-admin → /.
 */
export async function requireAdmin(
  queryClient: import("@tanstack/react-query").QueryClient,
  href: string,
): Promise<{ user: CurrentUser }> {
  const { user } = await requireAuth(queryClient, href);
  if (user.role !== "admin") {
    throw redirect({ to: "/" });
  }
  return { user };
}
